import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { BUTTON_CLICK } from 'src/app/components/shared/modal/exit-modal/exit-modal.constants';
import { WarningModalInput } from 'src/app/components/shared/modal/warning-modal/warning-modal.constants';
import { SORTS_TYPES } from 'src/app/components/shared/table/outbound-scan-table/outbound-scan-table.constant';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { DataUtils } from 'src/app/core/utils/data/data.utils';
import { CONST, ULD_FORM_Ids } from '../outbound-scan-gle.constant';
import { OutboundScan } from 'src/app/core/models/position-operations/outbound-gle.model';

@Component({
  selector: 'app-close-uld-modal',
  templateUrl: './close-uld-modal.component.html',
  styleUrls: ['./close-uld-modal.component.scss'],
  standalone: false,
})
export class CloseUldModalComponent implements OnInit {
  readonly CONST = {
    ...CONST.ULD_MODAL,
    ...CONST.TRANSFER_ULD_MODAL,
    ...CONST.CLOSE_ULD,
    ...CONST,
  };
  readonly IDS = ULD_FORM_Ids;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  closeUldForm!: FormGroup;
  warningDialogData: WarningModalInput = {
    header: this.CONST.MODAL.HEADER,
    body: `${this.CONST.MODAL.LINE1} <br/> ${this.CONST.MODAL.LINE2}`,
    confirmButtonText: this.CONST.MODAL.CONFIRM_BUTTON,
    rejectButtonText: this.CONST.MODAL.REJECT_BUTTON,
  };
  constructor(
    private readonly fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA)
    public data: { rowData: OutboundScan; index: number; percentage: number },
    public dialogRef: MatDialogRef<CloseUldModalComponent>,
    private readonly dialog: MatDialog,
    private readonly dataUtils: DataUtils,
    private commonUtils: CommonUtils,
  ) {}
  ngOnInit(): void {
    this.initForm();
  }

  /**
   * initialize form
   *
   * @memberof CloseUldModalComponent
   */
  initForm() {
    const rowData = this.data?.rowData ?? ({} as OutboundScan);

    let formatedDate: string | null = null;
    if (rowData?.createSortDate) {
      const parsedDate = new Date(rowData.createSortDate);
      if (!isNaN(parsedDate.getTime())) {
        formatedDate = this.getFormatedDate(parsedDate);
      }
    }

    const sort = this.getSort(rowData?.sort) || rowData?.sort;
    this.closeUldForm = this.fb.group({
      [ULD_FORM_Ids.SORT_DATE]: [formatedDate],
      [ULD_FORM_Ids.SORT]: [sort],
      [ULD_FORM_Ids.LOAD]: [rowData?.loadName],
      [ULD_FORM_Ids.EQUIPMENT_NUMBER]: [rowData?.equipmentNumber],
      [ULD_FORM_Ids.PERCENTAGE]: [
        this.data?.percentage,
        Validators.compose([Validators.required]),
      ],
    });
  }

  /**
   * Used to get formated date yyyy-mm-dd
   *
   * @param {Date} date
   * @return {*}
   * @memberof CloseUldModalComponent
   */
  getFormatedDate(date: Date) {
    const formatedDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split('T')[0];
    return formatedDate;
  }

  /**
   * Returns Sort for given accronyms
   *
   * @param {*} sort
   * @return {*}  {string}
   * @memberof CloseUldModalComponent
   */
  getSort(sort: SORTS_TYPES | string): string {
    switch (sort) {
      case SORTS_TYPES.SUNRISE:
        return 'S';
      case SORTS_TYPES.MORNING:
        return 'M';
      case SORTS_TYPES.DAY:
        return 'D';
      case SORTS_TYPES.TWILIGHT:
        return 'T';
      case SORTS_TYPES.NIGHT:
        return 'N';
      case SORTS_TYPES.PRELOAD:
        return 'P';
      case SORTS_TYPES.LOCAL:
        return 'L';
      case SORTS_TYPES.AFTERNOON:
        return 'A';
      default:
        return '';
    }
  }
  /**
   * used to close modal and reopen popup if REJECT button click
   *
   * @param {string} event
   * @memberof CloseUldModalComponent
   */
  onClose(event: string) {
    this.dialogRef.close(this.closeUldForm.value);

    if (event === BUTTON_CLICK.REJECT) {
      return this.reOpenCloseULDPopup();
    }
  }

  /**
   * Used reopen close ULD popup
   *
   * @memberof CloseUldModalComponent
   */
  reOpenCloseULDPopup() {
    const percentage = this.closeUldForm.get('percentage')?.value;
    const deleteData = { delete: true, percentage, index: this.data.index };
    this.dialog
      .open(CloseUldModalComponent, {
        width: '34.25rem',
        disableClose: true,
        autoFocus: false,
        data: {
          ...this.data,
          percentage: this.closeUldForm.get('percentage')?.value,
        },
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp && resp?.delete) {
          this.dataUtils.deleteUld$.next(deleteData);
        }
      });
  }

  /**
   * used to show cancel warning modal
   *
   * @memberof CloseUldModalComponent
   */
  cancel() {
    this.commonUtils
      .openWarningModal({
        header: this.CONST.CANCEL_MODAL.HEADER,
        body: this.CONST.CANCEL_MODAL.BODY,
        confirmButtonText: this.CONST.CANCEL_MODAL.CONFIRM_BUTTON,
        rejectButtonText: this.CONST.CANCEL_MODAL.REJECT_BUTTON,
        extraClass: 'large-confirm-btn',
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.dialogRef.close();
        }
      });
  }
  /**
   * used to submit form value close modal
   *
   * @memberof CloseUldModalComponent
   */
  submit() {
    const percentage = this.closeUldForm.get('percentage')?.value;
    this.dialogRef.close({ delete: true, percentage, index: this.data.index });
  }
}
